import React, { createContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { A, Button, H3, H5, HR, P } from '@upsolve/ui';

import reviewsJSON from '../../../content/onBuild/reviewData.json';
import BlobSherlock from '../../../static/images/blobs/sherlock.svg';
import Portal from './Portal';
import ModalBackground from './ModalBackground';
import trackComponentAction from '../analytics/trackComponentAction';
import trackComponentImpression from '../analytics/trackComponentImpression';
import getScreenerUrl from '../Links/getScreenerUrl';
import SCREENER_CTA_TYPES from '../analytics/screenerCtaTypes';
import { ButtonLink } from '../analytics/LinksWithOnClick';
import ReviewTile from '../reviews/ReviewTile';

const trackingProps = {
  componentName: 'BankruptcyTruthsModal',
  componentVersion: 0,
};

const StyledBankruptcyTruthItem = styled.div`
  background: ${props => props.theme.colors.white[900]};
  border: 1px solid ${props => props.theme.colors.white[500]};
  border-bottom: 2px solid ${props => props.theme.colors.white[700]};
  border-radius: 8px;
  & > div.top {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 1em 1.5em;
    border-bottom: 1px solid ${props => props.theme.colors.white[500]};
    font-size: 20px;
    font-weight: 500;
    color: ${props => props.theme.colors.brand[500]};
    label {
      font-size: 11px;
      font-weight: 500;
      color: ${props => props.theme.colors.white[100]};
    }
  }
  div.myth {
    width: 100%;
    padding: 1em 1.5em;
    ol {
      list-style-type: none;
      padding-left: 0.25em;
      li {
        font-size: 16px;
        line-height: 140%;
        margin-bottom: 0.75em;
      }
    }
  }
  @media(max-width: ${props => props.theme.breakpoints[500]}) {
    & > div.top {
      padding: 1em 0.75em;
    }
    & > div.myth {
      padding: 1em 0.75em;
    }
  }
`;

const StyledBankruptcyTruthsModal = styled.div`
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 5000;
  div.bk-truths-modal__panel {
    width: 100%;
    max-width: 800px;
    margin: 0 auto;
    padding: 2em;
    background: ${props => props.theme.colors.white[800]};
    border: 1px solid ${props => props.theme.colors.white[500]};
    border-bottom: 2px solid ${props => props.theme.colors.white[300]};
    border-radius: 8px;
  }
  div.bk-truths-modal__panel {
    &__header {
      display: flex;
      flex-direction: column;
      align-items: center;
      & > svg {
        max-height: 150px;
      }
      text-align: center;
      h3 {
        color: ${props => props.theme.colors.brand[500]};
      }
      p {
        max-width: 540px;
        margin: 0 auto;
        font-weight: 500;
      }
    }
    &__footer {
      p {
        text-align: center;
      }
      a, button, p {
        font-weight: 500;
        margin-bottom: 0.75em;
      }
    }
    & > div {
      margin-bottom: 2em;
    }
    .review-break {
      width: 100%;
      padding: 0;
      & > div {
        width: 100%;
        min-width: 100%;
        margin: 0;
      }
    }
  }
  @media(max-width: ${props => props.theme.breakpoints[500]}) {
    div.bk-truths-modal__panel {
      padding: 1em 0.25em;
      div.review-break {
        padding: 0 1em;
      }
    }
  }
`;

const reviewsToUse = {
  accurate: reviewsJSON.google
    .find(r => r.reviewText && r.reviewText.includes('Such an amazing tool to help when you need it most. ')),
  credit: reviewsJSON.google
    .find(r => r.reviewText && r.reviewText.includes('I can\'t say enough good things about Upsolve. I finally decided that filing bankruptcy was the only choice I had and')),
  complex: reviewsJSON.google
    .find(r => r.reviewText && r.reviewText.includes('I highly recommend this non-profit organization dedicated to helping persons with low income achieve a financial fresh start.')),
  community: reviewsJSON.facebook
    .find(r => r.reviewText && r.reviewText.includes('I was skeptical that I\'d be in debt for decades because I couldn\'t afford to file bankruptcy. Upsolve and the community helped me every step of the way')),
  embarrassed: reviewsJSON.facebook
    .find(r => r.reviewText && r.reviewText.includes('Honestly skeptical at first. But Using Upsolve from start to finish made this anxiety-filled process much less intimidating and easier at the comfort of my home')),
  experienced: reviewsJSON.google
    .find(r => r.reviewText && r.reviewText.includes('Awesome and no playing around, just the facts,')),
  experiencedTwo: reviewsJSON.google
    .find(r => r.reviewText && r.reviewText.includes('I have had such a positive experience with Kristin, John, Rohan, and Ben to name a few')),
  fast: reviewsJSON.google
    .find(r => r.reviewText && r.reviewText.includes('Great experience, fast and efficient. They went through all the questions and had paperwork ready for me to review and send off in a very short time. ')),
  flawless: reviewsJSON.google
    .find(r => r.reviewText && r.reviewText.includes('this is by far the best and most complete')),
  friendly: reviewsJSON.google
    .find(r => r.reviewText && r.reviewText.includes('I just can’t get over how friendly and warm the staff')),
  legalAidOne: reviewsJSON.google
    .find(r => r.reviewText && r.reviewText.includes('help a lot more people in Vermont wh')),
  legalAidTwo: reviewsJSON.google
    .find(r => r.reviewText && r.reviewText.includes('Community Legal Services of Mid-Florida,')),
  nervous: reviewsJSON.google
    .find(r => r.reviewText && r.reviewText.includes('I was so nervous and upset and they walked me right through everything step by step')),
  onMyOwn: reviewsJSON.google
    .find(r => r.reviewText && r.reviewText.includes('I couldn\'t afford an attorney. but because of Upsolve, I filed my bankruptcy on my own!')),
  onMyOwnTwo: reviewsJSON.google
    .find(r => r.reviewText && r.reviewText.includes('I was struggling to fill out the many schedules and forms on my own. I found upsolve because of their extensive online help resources')),
  prepared: reviewsJSON.google
    .find(r => r.reviewText && r.reviewText.includes('I was absolutely scared to do my bankruptcy on my own since I couldn\'t afford an attorney. Upsolve was with me the whole way')),
  scared: reviewsJSON.google
    .find(r => r.reviewText && r.reviewText.includes('I was so scared to file bankruptcy on my own, but I could not afford an Attorney')),
  tooGood: reviewsJSON.google
    .find(r => r.reviewText && r.reviewText.includes('This service is incredible! With the free help of Upsolve all of my consumer debts were discharged!!! I was only able to accomplish this because of Upsolve.')),
};

const BKTruthItem = ({ children, headline }) => {
  const [showTruth, setShowTruth] = useState(false);
  function mythReveal() {
    trackComponentAction({ ...trackingProps, actionId: 'bankruptcyTruthsModal.mythReveal', actionMethod: 'click' })
    setShowTruth(true);
  }

  return (
    <StyledBankruptcyTruthItem>
      <div className="top">
        <label>MYTH</label>
        {headline}
      </div>
      <div className="myth">
        {showTruth
          ? children
          : (
            <div className="toggler">
              <Button buttonType="white" width="100%" onClick={() => mythReveal()}>
                {showTruth ? 'Close answers' : 'Reveal the truth!'}
              </Button>
            </div>
          )}
      </div>
    </StyledBankruptcyTruthItem>
  );
}

/**
 * BankruptcyTruthsModal
 * - v0: Where we began.
 *
 * @component
 * @version 0
 */
const BankruptcyTruthsModal = (props) => {
  useEffect(() => {
    trackComponentImpression(trackingProps);
  }, []);

  return (
    <StyledBankruptcyTruthsModal>
      <ModalBackground onClick={() => props.onClose()}>
        <div
          className="bk-truths-modal__panel"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="bk-truths-modal__panel__header">
            <BlobSherlock />
            <H3>Bankruptcy Myth Busters</H3>
            <P>
              Read what Upsolve and users have to say about the most common chapter 7 bankruptcy misconceptions:
            </P>
          </div>
          <HR />
          <BKTruthItem headline="This seems too good to be true.">
            <ol>
              <li><b>We get it. Not many things are really free, and you may think there is a "catch." There is not.</b> We started our nonprofit because we believe that everyone should be able to access their legal rights, even if they can’t afford a lawyer.</li>
              <li>One of our co-founders got the idea for Upsolve while working as a researcher at Harvard Law School. At first, we were a brick-and-mortar clinic in NYC, and we shifted to an app because it allows us to help many more people. Since then, we’ve raised about $3 million in grants and donations to support our nonprofit and keep our app totally free.</li>
              <li>You can also join our Facebook group to meet thousands of other Upsolve users, read media articles about us in places like Forbes, the New York Times, and Wall Street Journal, or see the real faces behind our team.</li>
            </ol>
            {reviewsToUse.community && <ReviewTile review={reviewsToUse.community} hideReviewDate showReviewFully />}
          </BKTruthItem>
          <BKTruthItem headline="I don’t know if bankruptcy is the right option for me.">
            <ol>
              <li>We know it’s an important decision and bankruptcy isn't right for everyone. People often file bankruptcy when they have more debt than they’ll ever be able to pay back. We have articles to help you learn more.</li>
              <li>You can also join our Facebook group to learn more about why other people decided to file bankruptcy. If you want to talk to someone and get specific advice, you can call a private independent attorney before using our tool to file on your own. We can help you find an attorney for a free evaluation, or you can Google around, as most attorneys offer them.</li>
            </ol>
            {reviewsToUse.nervous && <ReviewTile review={reviewsToUse.nervous} hideReviewDate showReviewFully />}
          </BKTruthItem>
          <BKTruthItem headline="I’m afraid my bankruptcy will not be accepted if I file on my own.">
            <ol>
              <li>The vast majority of bankruptcy forms that our users have filed with the courts have been accepted. In the small handful of instances -- out of thousands of cases filed -- where bankruptcy forms were not accepted, the court clerk almost always explained the issue and gave the user a chance to fix it.</li>
            </ol>
            {reviewsToUse.onMyOwn && <ReviewTile review={reviewsToUse.onMyOwn} hideReviewDate showReviewFully />}
          </BKTruthItem>
          <BKTruthItem headline="I’m afraid of messing up and getting in trouble by doing this on my own.">
            <ol>
              <li>The vast majority of Upsolve users who use our tool to generate their forms receive a discharge.</li>
              <li>The three main reasons people don’t get a discharge are (1) they don’t show up to their brief meeting after they file with their bankruptcy trustee, (2) they don’t pay their filing fee to the court, or (3) they don’t take the 1-hour required post-filing financial education course. Virtually every Upsolve user who does these three things after they file receives a discharge. Even those who forget to do these things are almost always able to reopen their case and get a discharge.</li>
              <li>In the very small number of cases where a trustee sees an issue with someone’s bankruptcy forms, they usually give the Upsolve user a chance to fix the issue by filing an amendment.</li>
              <li>The only way we’ve seen an Upsolve user “get into trouble” is that they intentionally hid a car or some other expensive property by leaving it off their forms.</li>
            </ol>
            {reviewsToUse.onMyOwnTwo && <ReviewTile review={reviewsToUse.onMyOwnTwo} hideReviewDate showReviewFully />}
          </BKTruthItem>
          <BKTruthItem headline="I feel bad for not paying back my debt and filing bankruptcy.">
            <ol>
              <li>Bankruptcy is a legal right guaranteed by law. It exists for a reason. Life happens. What’s important is how you move forward. Indeed, many well-known figures in American history filed bankruptcy and then gone on to become successful. Experts estimate that over 30 million Americans have filed bankruptcy.</li>
              <li>There's no stigma for businesses seeking bankruptcy protection and there shouldn't be one for personal bankruptcy cases. Indeed, it’s sad that in America, large corporations can afford lawyers who help them navigate the law, while low-income and working class families cannot. One reason we started Upsolve is to empower people who can’t afford lawyers.</li>
              <li>Everyone in life has setbacks. Bankruptcy is a tool that allows you to pick yourself back up and get a fresh start, so you can begin working towards your goals again. Over 90% of bankruptcies occur after a medical emergency, job loss, or divorce. These are events beyond our control. Filing bankruptcy isn’t admitting defeat, it’s using a tool built for people who hit hard times, guaranteed by law.</li>
            </ol>
            {reviewsToUse.friendly && <ReviewTile review={reviewsToUse.friendly} hideReviewDate showReviewFully />}
          </BKTruthItem>
          <BKTruthItem headline="I’m embarrassed what my friends and family may think if I file bankruptcy.">
            <ol>
              <li>Everyone has encountered obstacles or a hard time in their life. What matters is that you’re taking charge and making improvements. Your loved ones should support you because they care about seeing you live a happy life in the future without being trapped by your debt.</li>
              <li>Also keep in mind that almost always, the only family and friends who find out about bankruptcy are the people you tell. It’s highly unlikely that your family or friends look through bankruptcy court records on their own, waiting to find the name of someone they know.</li>
            </ol>
            {reviewsToUse.embarrassed && <ReviewTile review={reviewsToUse.embarrassed} hideReviewDate showReviewFully />}
          </BKTruthItem>
          <BKTruthItem headline="I’m afraid I’ll lose things I own like my car or my clothes if I file.">
            <ol>
              <li>Over 95% of people who file Chapter 7 bankruptcy keep absolutely everything they own. “Exemptions” are laws that protect property during bankruptcy.</li>
            </ol>
            {reviewsToUse.prepared && <ReviewTile review={reviewsToUse.prepared} hideReviewDate showReviewFully />}
          </BKTruthItem>
          <BKTruthItem headline="I’m afraid my credit score will go down.">
            <ol>
              <li>In our experience, people with already low credit scores (usually below 600) see their credit scores improve shortly after filing bankruptcy, while people with higher credit scores see their credit decrease in the near term. Many Upsolve users get inundated with offers for credit by mail after they file, although it’s usually smart to get a secured credit card.</li>
              <li>Most Upsolve users who have a bankruptcy on their credit report, especially those who see their credit scores improve, are able to get access to credit in the near or medium term. Having a bankruptcy on your credit report doesn’t automatically mean you won’t have access to credit.</li>
            </ol>
            {reviewsToUse.credit && <ReviewTile review={reviewsToUse.credit} hideReviewDate showReviewFully />}
          </BKTruthItem>
          <BKTruthItem headline="I’m afraid Upsolve doesn’t work in my state.">
            <ol>
              <li>Upsolve users have filed a case in every state where we provide our tool.</li>
            </ol>
            {reviewsToUse.legalAidTwo && <ReviewTile review={reviewsToUse.legalAidTwo} hideReviewDate showReviewFully />}
            {reviewsToUse.legalAidOne && <ReviewTile review={reviewsToUse.legalAidOne} hideReviewDate showReviewFully />}
          </BKTruthItem>
          <BKTruthItem headline="I’m afraid I will not be able to buy a house in the future.">
            <ol>
              <li>Some people confuse bankruptcy being on your credit report with you not being able to access any credit. That’s not true. People are able to buy homes after bankruptcy, although you may have a hard time getting a mortgage in the near term.</li>
              <li>In our experience, people with already low credit scores (usually below 600) see their credit scores improve shortly after filing bankruptcy, while people with higher credit scores see their credit decrease for a little while.</li>
            </ol>
            {reviewsToUse.complex && <ReviewTile review={reviewsToUse.complex} hideReviewDate showReviewFully />}
          </BKTruthItem>
          <BKTruthItem headline="I’m afraid this process will take too long and I won’t be able to make time for it.">
            <ol>
              <li>We understand you may be balancing a job, family, or school. Making time can be tough. Keep in mind that many Upsolve users tell us that the process is faster and easier than they expected. It takes 3 hours to complete the tasks you need to generate your bankruptcy forms. If you have $30,000 in debt, that’s $10,000 in debt relieved per hour of your time.</li>
            </ol>
            {reviewsToUse.fast && <ReviewTile review={reviewsToUse.fast} hideReviewDate showReviewFully />}
          </BKTruthItem>
          <BKTruthItem headline="I don’t know anyone who has filed bankruptcy before and feel very lonely.">
            <ol>
              <li>We understand. That’s why we have a private Facebook group with thousands of Upsolve users. Hopefully, it’ll help you feel less alone.</li>
              <li>Also, keep in mind that bankruptcy is a lot more common than people think. Experts estimate that over 30 million people in America have filed bankruptcy. Over 90% of bankruptcies are tied to medical bills, job loss, and divorce. Life happens.</li>
            </ol>
            {reviewsToUse.community && <ReviewTile review={reviewsToUse.community} hideReviewDate showReviewFully />}
          </BKTruthItem>
          {reviewsToUse.tooGood && <ReviewTile review={reviewsToUse.tooGood} hideReviewDate showReviewFully />}
          <HR />
          <div className="bk-truths-modal__panel__footer">
            <P>Want to explore Upsolve's tool and content more? Take our screener to see if its a fit:</P>
            <ButtonLink
              size="lg"
              href={getScreenerUrl({ cta: SCREENER_CTA_TYPES.BANKRUPTCY_TRUTHS_MODAL, contentGroup: props.contentGroup })}
              onClick={() => {
                trackComponentAction({ ...trackingProps, actionId: 'bankruptcyTruthsModal.screenerNavigation', actionMethod: 'click' })
              }}
            >
              Take Upsolve Screener
            </ButtonLink>
            <Button
              size="lg"
              width="100%"
              onClick={() => props.onClose()}
            >
              Close Bankruptcy F.A.Q.
            </Button>
          </div>
        </div>
      </ModalBackground>
    </StyledBankruptcyTruthsModal>
    );
}

// Context/Provider for Usage anywhere
export const BankruptcyTruthsModalContext = createContext();
// - Provider component wrapper for TopLevelLayout
export const BankruptcyTruthsModalProvider = (props) => {
  const [showingBankruptcyTruths, setShowingBankruptcyTruths] = useState(false);
  return (
    <BankruptcyTruthsModalContext.Provider value={{ setShowingBankruptcyTruths }}>
      {props.children}
      {showingBankruptcyTruths && <Portal><BankruptcyTruthsModal onClose={() => setShowingBankruptcyTruths(false)} /></Portal>}
    </BankruptcyTruthsModalContext.Provider>
  );
};

export default BankruptcyTruthsModal;
